// src/components/RichTextEditor.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import './RichTextEditor.css';

const TOOLS = [
  { cmd: 'bold', icon: 'fa-bold', title: 'پررنگ (Ctrl+B)' },
  { cmd: 'italic', icon: 'fa-italic', title: 'کج (Ctrl+I)' },
  { cmd: 'underline', icon: 'fa-underline', title: 'زیرخط (Ctrl+U)' },
  'sep',
  { cmd: 'insertUnorderedList', icon: 'fa-list-ul', title: 'لیست نقطه‌ای' },
  { cmd: 'insertOrderedList', icon: 'fa-list-ol', title: 'لیست شماره‌دار' },
  'sep',
  { cmd: 'justifyRight', icon: 'fa-align-right', title: 'راست‌چین' },
  { cmd: 'justifyCenter', icon: 'fa-align-center', title: 'وسط‌چین' },
  { cmd: 'justifyLeft', icon: 'fa-align-left', title: 'چپ‌چین' },
  { cmd: 'justifyFull', icon: 'fa-align-justify', title: 'تراز دوطرفه' },
  'sep',
  { cmd: 'removeFormat', icon: 'fa-eraser', title: 'حذف قالب‌بندی', noState: true },
  { cmd: 'undo', icon: 'fa-rotate-left', title: 'بازگرداندن', noState: true },
  { cmd: 'redo', icon: 'fa-rotate-right', title: 'انجام مجدد', noState: true },
];

const isEmptyEl = (el) => !el.textContent.trim() && !el.querySelector('li');

export default function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'متن نامه را بنویسید...',
  minHeight = 260,
}) {
  const ref = useRef(null);
  const [active, setActive] = useState({});
  const [empty, setEmpty] = useState(!value);

  // مقدار بیرونی → ادیتور (فقط وقتی با محتوای فعلی فرق دارد؛ تا مکان‌نما نپرد)
  useEffect(() => {
    const el = ref.current;
    if (el && el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
      setEmpty(isEmptyEl(el));
    }
  }, [value]);

  // Enter → پاراگراف <p> به‌جای <div>
  useEffect(() => {
    try {
      document.execCommand('defaultParagraphSeparator', false, 'p');
    } catch (e) {
      /* ignore */
    }
  }, []);

  const refreshActive = useCallback(() => {
    const el = ref.current;
    const sel = window.getSelection();
    if (!el || !sel || !sel.anchorNode || !el.contains(sel.anchorNode)) return;
    const next = {};
    TOOLS.forEach((t) => {
      if (t.cmd && !t.noState) {
        try {
          next[t.cmd] = document.queryCommandState(t.cmd);
        } catch (e) {
          next[t.cmd] = false;
        }
      }
    });
    setActive(next);
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', refreshActive);
    return () => document.removeEventListener('selectionchange', refreshActive);
  }, [refreshActive]);

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    setEmpty(isEmptyEl(el));
    onChange?.(el.innerHTML);
  };

  const run = (cmd) => {
    ref.current?.focus();
    document.execCommand(cmd, false, null);
    emit();
    refreshActive();
  };

  // پیست فقط به‌صورت متن ساده (ظاهر Word/وب وارد نامه نشود)
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  return (
    <div className="rte">
      <div className="rte-toolbar">
        {TOOLS.map((t, i) =>
          t === 'sep' ? (
            <span key={`s${i}`} className="rte-sep" />
          ) : (
            <button
              key={t.cmd}
              type="button"
              className={`rte-btn ${active[t.cmd] ? 'on' : ''}`}
              title={t.title}
              aria-label={t.title}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => run(t.cmd)}
            >
              <i className={`fa-solid ${t.icon}`} />
            </button>
          )
        )}
      </div>

      <div
        ref={ref}
        className={`rte-area ${empty ? 'is-empty' : ''}`}
        contentEditable
        suppressContentEditableWarning
        dir="rtl"
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        style={{ minHeight }}
        onInput={emit}
        onBlur={emit}
        onPaste={handlePaste}
      />
    </div>
  );
}